import type { ReactNode } from 'react';
import { BrandMark } from '@dayon/design-system';

import './sign-in.css';

/**
 * The frame every signed-out console screen sits in.
 *
 * Extracted from `SignIn.tsx` when the console finally got a password reset:
 * `ForgotPassword` and `ResetPassword` are also rendered for somebody with no
 * session, and both had to look like the front door rather than like a bare
 * form on a gradient. Copying ninety lines of brand panel into each would have
 * been three places for the same claims to drift — and the claims on this
 * panel are exactly the ones that drifted before.
 *
 * ── THE CLAIMS ON IT ARE CHECKABLE, AND THAT WAS NOT ALWAYS SO ───────────
 *
 * Five of the eight telemetry lines this panel used to carry were untrue, and
 * two had legal weight: a hardcoded brand count that was wrong, a "99.99%
 * ledger integrity" nothing computes, "hardware key ready" for a codebase with
 * no WebAuthn, a BMM jurisdiction pass appearing nowhere else in the
 * repository, and an ISO/IEC 27001 certification that existed as one string on
 * one screen.
 *
 * What is left is checkable: every brand really does carry a licence
 * reference, this console really does require a second factor, and a sign-in
 * attempt really is recorded in `identity.auth_attempt`. Keeping that in ONE
 * file is the point of this component — a claim is easier to keep honest when
 * there is one copy of it.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="signin">
      {/* Brand panel with live security clearance badge and telemetry grid. */}
      <div className="signin-brand">
        <div className="signin-brand-content">
          <span className="signin-badge-pill">
            <span className="status-dot live" /> RESTRICTED ACCESS · LEVEL 4 COMMAND
          </span>

          <div className="signin-lockup">
            <BrandMark size={128} />
            <div className="signin-wordmark">
              NorthAlley<span className="signin-wordmark-accent">OPS</span>
            </div>
            <span className="signin-chip">B2B Core Operator Platform</span>
          </div>

          <div className="signin-pitch">
            <h1>Enterprise Operator Console</h1>
            <p className="text-muted">
              Mission control across gaming verticals, liquidity reserves, and multi-tenant compliance.
            </p>
          </div>

          {/*
            * FIVE OF THESE EIGHT LINES WERE NOT TRUE, and two of them were
            * claims with legal weight.
            *
            *   "Active Brands 4" — there are three. A hardcoded count, and
            *     wrong; a signed-out page has no session to scope one to
            *     anyway, and publishing the tenant count at the front door is
            *     a disclosure rather than a feature.
            *
            *   "Ledger Integrity 99.99% · Continuous reconciliation" — nothing
            *     computes this. There IS a reconciliation job, for SA, and it
            *     produces findings rather than a percentage.
            *
            *   "Hardware key ready" — there is no WebAuthn in this codebase.
            *     No `navigator.credentials`, no security-key enrolment, no
            *     column to store one. The second factor is TOTP.
            *
            *   "PAGCOR / BMM · Jurisdiction passed" — each brand carries a
            *     PAGCOR licence reference (`platform.brand.licence_ref`), so
            *     that half is grounded. BMM appears NOWHERE ELSE in this
            *     repository, and nothing records a jurisdiction having been
            *     passed. A test-lab certification is not something a login
            *     screen may assert on the lab's behalf.
            *
            *   "ISO/IEC 27001 Certified" — this string appeared exactly once in
            *     the whole repository: here. There is no audit, no scope
            *     statement, no certificate and no expiry behind it.
            *
            * What is left is checkable. Every brand really does carry a
            * licence reference, this console really does require a second
            * factor, and a sign-in attempt really is recorded —
            * `identity.auth_attempt`, written on success and on failure.
            */}
          <div className="signin-telemetry-grid">
            <div className="signin-telemetry-card">
              <div className="signin-telemetry-label">Tenancy</div>
              <div className="signin-telemetry-value">Per brand</div>
              <div className="signin-telemetry-meta">Scope follows your account</div>
            </div>
            <div className="signin-telemetry-card">
              <div className="signin-telemetry-label">Licensing</div>
              <div className="signin-telemetry-value">Per brand</div>
              <div className="signin-telemetry-meta">Each brand carries its own licence reference</div>
            </div>
            <div className="signin-telemetry-card">
              <div className="signin-telemetry-label">Sign-in</div>
              <div className="signin-telemetry-value">Second factor</div>
              <div className="signin-telemetry-meta">Required on every account</div>
            </div>
            <div className="signin-telemetry-card">
              <div className="signin-telemetry-label">Every action</div>
              <div className="signin-telemetry-value">Recorded</div>
              <div className="signin-telemetry-meta">Attempts and changes alike</div>
            </div>
          </div>

          <div className="signin-footnote text-subtle">
            <span>Session recorded for audit</span>
            <span>All figures in PHP · Asia/Manila</span>
          </div>
        </div>
      </div>

      {/* Right: whichever signed-out screen is mounted. */}
      <div className="signin-panel">
        <div className="signin-form">{children}</div>
      </div>
    </div>
  );
}
